import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import * as services from '../../../services';
import * as models from '../../../models';
import { TeamEditComponent } from 'app/dialogs';

@Component({
    selector: 'app-team-list',
    templateUrl: './team-list.component.html',
    styleUrls: ['./team-list.component.scss'],
    standalone: false
})
export class TeamListComponent implements OnInit, OnDestroy {

  tableLoading: boolean = true;
  deleteTeamsClick: boolean = false;

  listTeams: TeamListItem[] = null;
  selectedRows: Array<TeamListItem> = [];

  private users: models.User[] = [];
  private teamSub: Subscription;
  private companySub: Subscription;

  constructor(
    private teamService: services.TeamService
    , private userService: services.UserService
    , private helperService: services.HelperService
    , private notifierService: services.NotifierService
    , public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.getFromServer();

    //set up subscription to listen to company id changes
    this.companySub = this.helperService.getCurrentCompanyId().subscribe(() => {
      this.getFromServer();
    });
  }

  ngOnDestroy(): void {
    if (this.teamSub != null) {
      this.teamSub.unsubscribe();
    }
    if (this.companySub != null) {
      this.companySub.unsubscribe();
    }
  }

  async getFromServer() {
    this.tableLoading = true;
    this.selectedRows = [];
    this.listTeams = null;

    let companyId = this.helperService.currentCompanyId;
    if (companyId == null) {
      this.listTeams = [];
      this.tableLoading = false;
      return;
    }

    this.users = await this.userService.getUsersForCompanyWithCaching(companyId).catch(() => [] as models.User[]);

    //teams stream live so edits from the dialog show up without a manual refresh
    if (this.teamSub != null) {
      this.teamSub.unsubscribe();
    }

    this.teamSub = this.teamService.getAll().snapshotChanges().subscribe(snapshot => {
      let teams: models.Team[] = [];
      snapshot.forEach(element => {
        let team: models.Team = element.payload.doc.data();
        team.uid = element.payload.doc.id;
        teams.push(team);
      });

      this.listTeams = this.helperService.sortArrayByStringField(teams, 'name').map(team => this.toListItem(team));
      this.tableLoading = false;
    }, () => {
      this.tableLoading = false;
      this.notifierService.error('Unable to load Teams.');
    });
  }

  private toListItem(team: models.Team): TeamListItem {
    let userIds = team.userIds || [];
    let members = userIds.map(id => this.users.find(user => user.id === id)).filter(user => user != null);

    return {
      team: team,
      memberCount: userIds.length,
      memberNames: members.map(user => user.displayName || user.emailAddress).join(', '),
      avatars: members.slice(0, 4).map(user => ({
        initials: this.userInitials(user),
        name: user.displayName || user.emailAddress || 'User'
      })),
      overflow: Math.max(userIds.length - 4, 0)
    };
  }

  userInitials(user: models.User): string {
    const source = user.displayName || user.emailAddress || '?';
    const parts = source.trim().split(/\s+/).filter(Boolean);
    return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : source.slice(0, 2)).toUpperCase();
  }

  teamInitials(team: models.Team): string {
    const source = (team.name || '?').trim();
    const parts = source.split(/\s+/).filter(Boolean);
    return (parts.length > 1 ? parts[0][0] + parts[1][0] : source.slice(0, 2)).toUpperCase();
  }

  addTeam() {
    this.dialog.open(TeamEditComponent, {
      panelClass: 'med-width-dialog',
      data: null
    });
  }

  editTeam(item: TeamListItem) {
    this.dialog.open(TeamEditComponent, {
      panelClass: 'med-width-dialog',
      data: item.team
    });
  }

  deleteSelectedRowsClick(item?: TeamListItem) {
    let rows = item ? [item] : this.selectedRows;
    if (rows.length === 0) { return; }

    let message = rows.length === 1
      ? `Are you sure you want to delete ${rows[0].team.name}?  This will permanently delete it and all of its data.`
      : `Are you sure you want to delete the ${rows.length} selected Teams?  This will permanently delete them and all of their data.`;

    this.notifierService.confirm('Delete Teams', message,
      () => { this.deleteRows(rows) },
      () => { });
  }

  private async deleteRows(rows: TeamListItem[]) {
    this.deleteTeamsClick = true;

    try {
      await Promise.all(rows.map(row => this.teamService.delete(row.team.uid)));
      this.selectedRows = [];
      this.notifierService.success(`Successfully deleted ${rows.length === 1 ? 'Team' : 'Teams'}`);
    } catch (error) {
      this.notifierService.error('Unable to delete the Teams selected.');
    }

    this.deleteTeamsClick = false;
  }

  export() {
    const esc = (value: any) => '"' + String(value ?? '').replace(/"/g, '""') + '"';
    const lines = [['Team Name', 'Members', 'Member Names'].map(esc).join(',')];
    (this.listTeams || []).forEach(item => lines.push([
      item.team.name, item.memberCount, item.memberNames
    ].map(esc).join(',')));
    const url = URL.createObjectURL(new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'TeamList_Export.csv';
    link.click();
    URL.revokeObjectURL(url);
  }
}

export interface TeamListItem {
  team: models.Team;
  memberCount: number;
  memberNames: string;
  avatars: Array<{ initials: string, name: string }>;
  overflow: number;
}
